import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Modal, Form, DatePicker, Select, Input, Alert, Descriptions, Table, Tag, Checkbox, Button, Space, Spin,
  Popconfirm, Typography, message,
} from 'antd';
import { PlusOutlined, WarningOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useModuleApi } from '../ortak/moduleApi';
import { useFinanceApi } from '../finans/api/financeApi';
import { fmtAmount, fmtDate } from '../finans/shared/format';

// 26.10 (F6): YS kira ödeme talebi — fatura no / fatura tarihi SORULMAZ; yalnız ödeme vadesi
// (+ cari kartı, IBAN, açıklama). Talep yeni Fedora Finans'a (nf_odeme_talebi) düşer.
// Bu dosya ayrıca YsFaturaTab ve BankaFaturaIslemPage'in ortak parçalarını dışa aktarır.
// 27.10 (inceleme): güvenilmeyen bazda tutar önerilmez ('tutarı kontrol ettim' onayı), 'bilgi'
// seviyeli uyarılar engel değildir, Finans'ta silinmiş ödeme talebi yeniden açılabilir, 'bu adla
// cari kartı aç' benzer kartları gösterir (mükerrer kart açılmasın).
// 27.10 düzeltmesi: 'sözleşme toplamı' desenindeki sözleşmede (kira bedeli her lokasyonda sözleşme toplamı)
// referansın lokasyon payı olduğu ve tek ödeme olabileceği yazılır; diğer lokasyon talepleri lokasyonuyla listelenir.

const { Text } = Typography;

export const AY_ADLARI = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

/** HTTPException detail'i metin ya da {mesaj, ...} olabilir. */
export const hataMesaji = (e, varsayilan = 'İşlem başarısız') => {
  const d = e?.response?.data?.detail;
  if (!d) return varsayilan;
  if (typeof d === 'string') return d;
  return d.mesaj || varsayilan;
};

/** 409 + {kod, mesaj, uyarilar|cakisanlar} → onay gerektiren çakışma mı? */
export const onayGerekenCakisma = (e) => {
  const d = e?.response?.data?.detail;
  return e?.response?.status === 409 && d && typeof d === 'object' ? d : null;
};

/** Finans cari kartları (nf_cari) — /finance/lookups. */
export function useCariSecenekleri(aktif = true) {
  const finApi = useFinanceApi();
  const [cariler, setCariler] = useState([]);
  const [loading, setLoading] = useState(false);
  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const lk = await finApi.lookups();
      setCariler(lk?.cari || []);
      return lk?.cari || [];
    } catch {
      message.error('Finans cari listesi alınamadı');
      return [];
    } finally {
      setLoading(false);
    }
  }, [finApi]);
  useEffect(() => { if (aktif) reload(); }, [aktif, reload]);
  const options = useMemo(() => cariler.map((c) => ({
    value: c.id, label: `${c.ad}${c.kod ? ` (${c.kod})` : ''}`, iban: c.iban,
  })), [cariler]);
  return { cariler, options, loading, reload, finApi };
}

const adNorm = (v) => String(v || '').toLocaleLowerCase('tr-TR').replace(/[^0-9a-zçğıöşü\s]/g, ' ')
  .split(/\s+/).filter(Boolean);

/** Benzer adlı cari kartları (ilk iki anlamlı kelimenin ilk 5 harfi geçen) — mükerrer kart uyarısı. */
export function benzerCariler(options, ad, limit = 5) {
  const kelimeler = adNorm(ad).filter((w) => w.length >= 3).slice(0, 2).map((w) => w.slice(0, 5));
  if (!kelimeler.length) return [];
  const tam = adNorm(ad).join(' ');
  return (options || []).filter((o) => {
    const lbl = adNorm(o.label || o.ad).join(' ');
    return lbl !== tam && kelimeler.every((k) => lbl.includes(k));
  }).slice(0, limit);
}

/** Popconfirm başlığı: benzer kart varsa önce onları gösterir. */
export const cariAcBaslik = (ad, benzerler) => (benzerler && benzerler.length
  ? `Benzer kart(lar) var: ${benzerler.map((b) => b.label || b.ad).join(' · ')}. Yine de '${ad}' adıyla YENİ cari kartı açılsın mı?`
  : `'${ad}' adıyla Finans'ta yeni cari kartı açılsın mı?`);

/**
 * Tutar kontrolü (27.10): baz güvenilir değilse calc tutar önermez; girilen toplam hesaplanandan
 * %10'dan fazla farklıysa da açık onay gerekir. Sunucu aynı kuralı uygular (409 'tutar_kontrol').
 */
export function tutarKontrolNedeni(data, toplam) {
  if (!data) return null;
  if (data.tutar_onerildi === false) return `Tutar önerilmedi — baz güvenilir değil (${data.baz_kaynagi || 'baz yok'})`;
  if (data.siklik_mesaji) return data.siklik_mesaji;
  const ref = Number(data.fatura_tutar) || 0;
  if (ref > 0 && Math.abs((Number(toplam) || 0) - ref) / ref > 0.10) return 'Girilen toplam hesaplanandan %10\'dan fazla farklı';
  return null;
}

/** Referans tutarı hissedarlara dağıtır (yalnız kullanıcı tıklarsa). */
export const referansDagit = (hissedarlar) => hissedarlar.map((h) => ({ ...h, tutar: h.referans_tutar ?? h.tutar }));

export function TutarKontrolKutusu({ neden, data, onay, setOnay, onDagit }) {
  if (!neden) return null;
  return (
    <Alert type="warning" showIcon style={{ margin: '8px 0' }} message={neden}
      description={(
        <Space direction="vertical" size={4}>
          {data?.tutar_onerildi === false && (
            <span>
              Referans (yalnız bilgi): <b>{fmtAmount(data.referans_tutar)}</b>
              {data.banka_sayisi > 1 ? ` (sözleşme toplamı ${data.banka_sayisi} bankaya bölündü)` : ''}
              {data.sozlesme_toplami_n > 1
                ? ` (sözleşme toplamı ${fmtAmount(data.sozlesme_toplami)} / ${data.sozlesme_toplami_n} lokasyon — tek ödeme olabilir, toplu ödeme kaydını kontrol edin)`
                : ''}.
              Önceki dönem ödemelerini kontrol edip hissedar tutarlarını girin.
              {onDagit && <Button size="small" style={{ marginLeft: 8 }} onClick={onDagit}>Referansı hissedarlara dağıt</Button>}
            </span>
          )}
          <Checkbox checked={onay} onChange={(e) => setOnay(e.target.checked)}>
            <Text type="danger">Tutarı eski ödemeler / sözleşme ile kontrol ettim</Text>
          </Checkbox>
        </Space>
      )} />
  );
}

/** 'Bu adla cari kartı aç' — Finans'ın mevcut cari oluşturma ucu (POST /finance/records/cari). */
export async function cariKartiAc(finApi, ad, iban) {
  const res = await finApi.create('cari', {
    ad: String(ad || '').trim(), tip: 'Yer Sahibi', active: 1, ...(iban ? { iban: String(iban).replace(/\s/g, '') } : {}),
  });
  return res?.data?.id;
}

const ODEME_KOLONLARI = [
  { title: 'Tarih', dataIndex: 'tarih', width: 95, render: fmtDate },
  { title: 'Tutar', dataIndex: 'tutar', width: 120, render: (v) => fmtAmount(v) },
  { title: 'Hesap Sahibi', dataIndex: 'hesapSahibi', ellipsis: true },
  { title: 'Eşleşme', dataIndex: 'eslesme', width: 110 },
  { title: 'Açıklama', dataIndex: 'aciklama', ellipsis: true },
];
const TALEP_KOLONLARI = [
  { title: '#', dataIndex: 'id', width: 70 },
  { title: 'Cari', dataIndex: 'cari_adi', ellipsis: true },
  { title: 'Dönem', dataIndex: 'donemBaslangic', width: 190, render: (v, r) => `${fmtDate(v)} - ${fmtDate(r.donemBitis)}` },
  { title: 'Tutar', dataIndex: 'fatura_tutar', width: 120, render: (v) => fmtAmount(v) },
  {
    title: 'Durum', key: 'd', width: 130, render: (_, r) => (r.odendi ? <Tag color="green">Ödendi</Tag>
      : r.odeme_talebi_id ? <Tag color={r.odeme_talebi_acik === 0 ? 'default' : 'blue'}>
        Ödeme talebi #{r.odeme_talebi_id}{r.odeme_talebi_acik === 0 ? ' (Finans\'ta silinmiş)' : ''}</Tag>
        : <Tag color="gold">Talep{Number(r.ekleyen) === 0 ? ' (toplu yükleme)' : ''}</Tag>),
  },
];
// 27.10: 'mukerrer_sozlesme' (sözleşme toplamının diğer lokasyonları) satırlarında lokasyon da gösterilir
const LOKASYON_KOLONU = {
  title: 'Lokasyon', dataIndex: 'lokasyon', width: 180, ellipsis: true,
  render: (v, r) => `${v || ''}${r.lokasyon_ad ? ` - ${r.lokasyon_ad}` : ''}`,
};

/** Uyarı listesi: [{kod, seviye, mesaj, satirlar}] — satırlar odm_taksit ya da gft olabilir.
 * 27.10 (kullanıcı kararı): 'bilgi' seviyesindekiler şimdilik gösterilmez; yalnız onay isteyen engeller. */
export function UyariListesi({ uyarilar }) {
  const gorunen = (uyarilar || []).filter((u) => u.seviye !== 'bilgi');
  if (!gorunen.length) return null;
  return (
    <Space direction="vertical" style={{ width: '100%' }} size={8}>
      {gorunen.map((u, i) => {
        const satirlar = u.satirlar || [];
        const odmMi = satirlar.length && satirlar[0].tarih !== undefined && satirlar[0].hesapSahibi !== undefined;
        return (
          <Alert key={`${u.kod}-${i}`} type={{ engel: 'error', bilgi: 'info' }[u.seviye] || 'warning'} showIcon
            message={u.mesaj}
            description={satirlar.length ? (
              <Table size="small" rowKey="id" pagination={false} dataSource={satirlar}
                columns={odmMi ? ODEME_KOLONLARI
                  : (u.kod === 'mukerrer_sozlesme' ? [LOKASYON_KOLONU, ...TALEP_KOLONLARI] : TALEP_KOLONLARI)}
                scroll={{ y: 180 }} />
            ) : null} />
        );
      })}
    </Space>
  );
}

/** 409 (mükerrer / eski Fedora çift ödeme) → kullanıcıya göster, açık onayla force ile yeniden gönder. */
export async function cakismaOnayliGonder(modal, gonder) {
  try {
    return await gonder(false);
  } catch (e) {
    const d = onayGerekenCakisma(e);
    if (!d) throw e;
    const uyarilar = d.uyarilar || (d.cakisanlar
      ? [{ kod: d.kod, seviye: 'engel', mesaj: 'Örtüşen dönemdeki talepler', satirlar: d.cakisanlar }] : []);
    const onay = await new Promise((resolve) => {
      (modal || Modal).confirm({
        title: 'Onay gerekli', icon: <WarningOutlined style={{ color: '#cf1322' }} />, width: 760,
        content: (
          <div>
            <p>{d.mesaj}</p>
            <UyariListesi uyarilar={uyarilar} />
            <p style={{ marginTop: 8 }}><b>Çift ödeme olmadığını kontrol ettiyseniz</b> devam edin; kayıt onayınızla açılır.</p>
          </div>
        ),
        okText: 'Kontrol ettim, yine de oluştur', okButtonProps: { danger: true }, cancelText: 'Vazgeç',
        onOk: () => resolve(true), onCancel: () => resolve(false),
      });
    });
    if (!onay) return null;
    return gonder(true);
  }
}

/** 'Artış ne zaman?' — /contracts/ys-invoice/calc yanıtının açıklama alanları. */
export function HesapAciklamasi({ data }) {
  if (!data) return null;
  const adimlar = data.artis_adimlari || [];
  return (
    <>
      <Descriptions size="small" column={2} bordered style={{ marginBottom: 8 }}>
        <Descriptions.Item label="Artış Ayı">
          {AY_ADLARI[(data.artis_ayi || 1) - 1]}
          {data.artis_ayi_turetilmis && <Tag color="orange" style={{ marginLeft: 6 }}>türetilmiş (başlangıç ayı)</Tag>}
        </Descriptions.Item>
        <Descriptions.Item label="Sonraki Artış">{fmtDate(data.sonraki_artis_tarihi)}</Descriptions.Item>
        <Descriptions.Item label="Endeks Ayı">{data.endeks_ay}</Descriptions.Item>
        <Descriptions.Item label="Bu Dönem Artış">%{data.artis_orani}</Descriptions.Item>
        <Descriptions.Item label="Baz" span={2}>
          {fmtAmount(data.baz_tutar)} <Text type="secondary">({data.baz_kaynagi}; {data.baz_yili})</Text>
          {!data.baz_guvenilir && <Tag color="red" style={{ marginLeft: 6 }}>kontrol edin</Tag>}
        </Descriptions.Item>
        <Descriptions.Item label="Önceki Dönem (yıllık)">{data.onceki_tutar != null ? fmtAmount(data.onceki_tutar) : '-'}</Descriptions.Item>
        <Descriptions.Item label="Hesaplanan (yıllık)">{fmtAmount(data.hesaplanan_kira)}</Descriptions.Item>
        <Descriptions.Item label="Ödeme Şekli">
          {data.aylik_mi ? <Tag color="purple">Aylık — {fmtAmount(data.aylik_tutar)}/ay</Tag> : 'Yıllık'}
          {data.manuel_artis && <Tag color="red">manuel artış</Tag>}
        </Descriptions.Item>
        <Descriptions.Item label="Dönem / Gün">{`${fmtDate(data.donem_baslangic)} - ${fmtDate(data.donem_bitis)} (${data.gun})`}</Descriptions.Item>
        <Descriptions.Item label={data.tutar_onerildi === false ? 'Referans Tutar (önerilmedi)' : 'Toplam Tutar'} span={2}>
          <b>{fmtAmount(data.fatura_tutar)}</b>
          {data.tutar_onerildi === false && <Tag color="red" style={{ marginLeft: 6 }}>tutarı elle girin</Tag>}
        </Descriptions.Item>
      </Descriptions>
      {adimlar.length > 0 && (
        <Table size="small" rowKey="yil" pagination={false} style={{ marginBottom: 8 }} dataSource={adimlar}
          columns={[
            { title: 'Artış Yılı', dataIndex: 'yil', width: 90 },
            { title: 'Endeks Ayı', dataIndex: 'endeks_ay', width: 100 },
            { title: 'Oran', dataIndex: 'oran', width: 90, render: (v, r) => (r.uygulandi ? `%${v}` : <Tag>uygulanmadı</Tag>) },
            { title: 'Yıllık Tutar', dataIndex: 'tutar', render: (v) => fmtAmount(v) },
          ]} />
      )}
      {(data.uyarilar || []).length > 0 && (
        <Alert type="warning" showIcon style={{ marginBottom: 8 }}
          message={<ul style={{ margin: 0, paddingLeft: 18 }}>{data.uyarilar.map((u, i) => <li key={i}>{u}</li>)}</ul>} />
      )}
    </>
  );
}

/** Mevcut talepten (gft) ödeme talebi aç. props: {open, talepId, onClose, onDone} */
export default function OdemeTalebiModal({ open, talepId, onClose, onDone }) {
  const api = useModuleApi('/contracts');
  const [form] = Form.useForm();
  const [prev, setPrev] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [onay, setOnay] = useState(false);
  const [sunucuUyari, setSunucuUyari] = useState(null);
  const [cariAciliyor, setCariAciliyor] = useState(false);
  const { options: cariOpts, loading: cariLoading, reload: cariReload, finApi } = useCariSecenekleri(open);
  const benzerler = useMemo(() => {
    const ad = prev?.talep?.cari_adi;
    const sunucu = (prev?.cari_benzer || []).map((c) => ({ value: c.id, label: c.ad, iban: c.iban }));
    const istemci = benzerCariler(cariOpts, ad, 8);
    const gorulen = new Set();
    return [...sunucu, ...istemci].filter((o) => (gorulen.has(o.value) ? false : gorulen.add(o.value))).slice(0, 8);
  }, [prev, cariOpts]);

  const yukle = useCallback(async () => {
    if (!talepId) return;
    setLoading(true);
    setPrev(null);
    setOnay(false);
    setSunucuUyari(null);
    try {
      const d = await api.get(`/ys-invoice/${talepId}/payment-request-preview`);
      setPrev(d);
      form.setFieldsValue({
        vade: d.vade_oneri ? dayjs(d.vade_oneri) : dayjs(),
        cari_id: d.cari_oneri?.id || undefined,
        iban: d.iban || '',
        aciklama: d.aciklama_oneri || '',
      });
    } catch (e) {
      message.error(hataMesaji(e, 'Talep bilgisi alınamadı'));
    } finally {
      setLoading(false);
    }
  }, [api, talepId, form]);

  useEffect(() => { if (open) yukle(); else setPrev(null); }, [open, yukle]);

  const uyarilar = sunucuUyari || prev?.uyarilar || [];
  const onayGerekli = (prev?.onay_gerekli || !!sunucuUyari) && uyarilar.some((u) => u.seviye === 'engel');

  const cariAc = async () => {
    setCariAciliyor(true);
    try {
      const id = await cariKartiAc(finApi, prev?.talep?.cari_adi, form.getFieldValue('iban'));
      await cariReload();
      if (id) form.setFieldsValue({ cari_id: id });
      message.success('Cari kartı açıldı');
    } catch (e) {
      message.error(e?.response?.status === 403
        ? 'Cari kartı açma yetkiniz yok — Finans → Cari ekranından açılmalı'
        : hataMesaji(e, 'Cari kartı açılamadı'));
    } finally {
      setCariAciliyor(false);
    }
  };

  const kaydet = async () => {
    let v;
    try { v = await form.validateFields(); } catch { return; }
    if (onayGerekli && !onay) { message.warning('Uyarıları kontrol edip onay kutusunu işaretleyin'); return; }
    setSaving(true);
    try {
      const res = await api.post(`/ys-invoice/${talepId}/payment-request`, {
        vade: dayjs(v.vade).format('YYYY-MM-DD'), cari_id: v.cari_id, iban: v.iban || null,
        aciklama: v.aciklama || null, force: onayGerekli && onay,
      });
      message.success(res?.message || 'Ödeme talebi oluşturuldu');
      if (onDone) onDone(res?.data);
    } catch (e) {
      const d = onayGerekenCakisma(e);
      if (d && d.uyarilar) {
        setSunucuUyari(d.uyarilar);
        setOnay(false);
        message.warning('Onay gerekli: uyarıları kontrol edin');
      } else {
        message.error(hataMesaji(e, 'Ödeme talebi oluşturulamadı'));
      }
    } finally {
      setSaving(false);
    }
  };

  const t = prev?.talep;
  return (
    <Modal title={`Ödeme Talebi Oluştur${t ? ` — ${t.cari_adi}` : ''}`} open={open} onCancel={onClose} width={820}
      onOk={kaydet} okText="Ödeme Talebi Oluştur" cancelText="Vazgeç" confirmLoading={saving}
      okButtonProps={{ disabled: !prev || !!prev.engel || (onayGerekli && !onay) }} destroyOnClose>
      <Spin spinning={loading}>
        {t && (
          <>
            <Descriptions size="small" column={2} bordered style={{ marginBottom: 12 }}>
              <Descriptions.Item label="Lokasyon">{`${t.lokasyon} - ${prev.lokasyon_ad || ''}`}</Descriptions.Item>
              <Descriptions.Item label="Tür">{t.fatura_turu_ad}{t.banka_ad ? ` / ${t.banka_ad} (kabin ${t.kabin})` : ''}</Descriptions.Item>
              <Descriptions.Item label="Dönem">{`${fmtDate(t.donemBaslangic)} - ${fmtDate(t.donemBitis)}`}</Descriptions.Item>
              <Descriptions.Item label="Tutar">
                <b>{fmtAmount(t.fatura_tutar)}</b>
                {prev.guncel_tutar && <Text delete type="secondary" style={{ marginLeft: 8 }}>{fmtAmount(prev.guncel_tutar.kayitli)}</Text>}
              </Descriptions.Item>
              <Descriptions.Item label="Firma">{prev.firma?.firma_adi || '-'}</Descriptions.Item>
              <Descriptions.Item label="İşlem Türü">{prev.islem_turu?.ad || <Tag color="red">bulunamadı</Tag>}</Descriptions.Item>
              <Descriptions.Item label="Talep Eden">{prev.talep_eden}</Descriptions.Item>
              <Descriptions.Item label="Defter">{prev.defter_yazilacak ? 'Aylık gider defteri yazılır' : 'Kesilmiş — defter yazılmaz'}</Descriptions.Item>
            </Descriptions>
            {prev.engel && <Alert type="error" showIcon style={{ marginBottom: 12 }} message={prev.engel} />}
            {/* 27.10: toplu yüklemenin geçmiş tutarı dönem hesabıyla (baz + endeks artışı) güncellendi */}
            {prev.guncel_tutar && <Alert type="info" showIcon style={{ marginBottom: 12 }} message={prev.guncel_tutar.mesaj} />}
            <UyariListesi uyarilar={uyarilar} />
            {onayGerekli && (
              <Checkbox checked={onay} onChange={(e) => setOnay(e.target.checked)} style={{ margin: '8px 0' }}>
                <Text type="danger">Uyarıları kontrol ettim; bu kira için çift ödeme olmadığını onaylıyorum.</Text>
              </Checkbox>
            )}
            <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
              <Space wrap align="start">
                <Form.Item name="vade" label="Ödeme Vadesi" rules={[{ required: true, message: 'Vade zorunlu' }]}>
                  <DatePicker format="DD.MM.YYYY" style={{ width: 160 }} />
                </Form.Item>
                <Form.Item name="cari_id" label="Cari Kartı (Finans)" rules={[{ required: true, message: 'Cari kartı seçin' }]}
                  style={{ minWidth: 380 }}>
                  <Select showSearch allowClear optionFilterProp="label" options={cariOpts} loading={cariLoading}
                    placeholder="Cari ara..." style={{ width: 380 }}
                    onChange={(id) => {
                      const o = cariOpts.find((x) => x.value === id);
                      if (o?.iban && !form.getFieldValue('iban')) form.setFieldsValue({ iban: o.iban });
                    }} />
                </Form.Item>
              </Space>
              {!prev.cari_oneri && (
                <Alert type="warning" showIcon style={{ marginBottom: 12 }}
                  message={`Finans'ta '${t.cari_adi}' adıyla birebir cari kartı yok.`}
                  description={(
                    <Space wrap>
                      {benzerler.map((c) => (
                        <Button key={c.value} size="small" onClick={() => form.setFieldsValue({ cari_id: c.value, iban: form.getFieldValue('iban') || c.iban || '' })}>
                          {c.label}
                        </Button>
                      ))}
                      <Popconfirm title={cariAcBaslik(t.cari_adi, benzerler)}
                        okText="Aç" cancelText="Vazgeç" onConfirm={cariAc}>
                        <Button size="small" type="dashed" icon={<PlusOutlined />} loading={cariAciliyor}>Bu adla cari kartı aç</Button>
                      </Popconfirm>
                    </Space>
                  )} />
              )}
              <Form.Item name="iban" label="IBAN" extra={prev.sozlesme_iban ? `Sözleşmedeki IBAN: ${prev.sozlesme_iban}` : undefined}>
                <Input placeholder="TR.." maxLength={34} />
              </Form.Item>
              <Form.Item name="aciklama" label="Açıklama">
                <Input.TextArea rows={2} />
              </Form.Item>
            </Form>
          </>
        )}
      </Spin>
    </Modal>
  );
}
